/**
 * What a principal may do with a collection, and through it with the works
 * inside.
 *
 * Ordered strongest first, which `strongestAccess` relies on. Unlike
 * `RoleKey`, these ARE stored — a grant is exactly the thing a role is derived
 * from — but they are still never accepted from a client as a claim about the
 * caller. The level the API returns is computed server-side from the same sets
 * the policies use, so the screen cannot offer an action the database will
 * refuse.
 *
 * These values must stay in step with the `ondo.access_level` enum in
 * supabase/migrations.
 */
export const ACCESS_LEVELS = ["owner", "curator", "viewer"];
export function isAccessLevel(value) {
    return (typeof value === "string" && ACCESS_LEVELS.includes(value));
}
/** May add and edit works. */
export function canCurate(level) {
    return level === "owner" || level === "curator";
}
/**
 * Holds the collection outright — the level `collection_grants` calls 'owner'.
 *
 * Separate from `canShare` so the two stay identical on purpose rather than by
 * coincidence, and so the frontend can ask "whose is this?" without asking
 * "may I share it?".
 */
export function isOwner(level) {
    return level === "owner";
}
/**
 * May share, rename or delete the collection.
 *
 * Sharing is an owner's right rather than a curator's, so that someone
 * invited to help catalogue cannot widen the audience they were let into.
 */
export function canShare(level) {
    return isOwner(level);
}
/**
 * May lend the work, or say where it is.
 *
 * Owner, matching `ondo.propose_loan` and `ondo.set_location`. Lending is
 * disposing of possession, and helping catalogue a collection must never imply
 * being able to send a piece of it to a museum.
 */
export function canLend(level) {
    return isOwner(level);
}
/** The strongest of several levels — a principal can hold one directly and inherit another. */
export function strongestAccess(levels) {
    return ACCESS_LEVELS.find((level) => levels.includes(level)) ?? null;
}
//# sourceMappingURL=collection.js.map